/* The rail, declared once. The simple view is not a second dashboard but a filter over this
 * one, so a panel added later behaves the same in both views until somebody says otherwise:
 *
 *   simpleLabel / simpleSub   replacement wording for the rail and the page title
 *   advanced: true            dropped in the simple view — developer material
 *   simpleOnly: true          hidden in the full view
 *   superadmin: true          only in the rail for a superadmin; the routes refuse an
 *                             admin regardless, so this is tidiness, not the lock
 *
 * Which view an account gets follows its role and nothing else; there is no switch.
 */

import type { ReactNode } from 'react';
import {
  Activity,
  CircleCheckBig,
  FileText,
  MessageCircle,
  Plug,
  Star,
  Ticket,
  TriangleAlert,
  UserPlus,
  Users,
} from 'lucide-react';

export interface Tab {
  /** Also the route: `/gaps`, `/coverage`, … */
  id: string;
  label: string;
  simpleLabel?: string;
  sub: string;
  simpleSub?: string;
  icon: ReactNode;
  advanced?: boolean;
  simpleOnly?: boolean;
  superadmin?: boolean;
}

export interface NavGroup {
  label: string;
  simpleLabel?: string;
  tabs: Tab[];
}

/* 16px inside the 28px tile the rail draws around every glyph (components/Shell.tsx).
 * The tile is the fixed part: the icon is sized to sit in it with room to spare. */
const icon = (Component: typeof Activity) => <Component className="h-4 w-4" />;

export const NAV: NavGroup[] = [
  {
    label: 'Start here',
    tabs: [
      {
        // In both views: the site-wide totals live on this page, so the full view needs it
        // too or a superadmin has nowhere to read them.
        id: 'summary',
        label: 'Overview',
        sub: 'What went well, what did not, and what to do about it',
        icon: icon(CircleCheckBig),
      },
    ],
  },
  {
    label: 'Improve',
    tabs: [
      {
        id: 'gaps',
        label: 'Gaps to fix',
        simpleLabel: 'Questions to fix',
        sub: 'Questions handled badly — the documentation to write next',
        simpleSub: 'Questions the assistant did not answer well',
        icon: icon(TriangleAlert),
      },
      {
        // One object, one page: reading the corpus, adding to it and finding out whether
        // that helped are one job. No simpleLabel — "Documents" needs no translating.
        id: 'documents',
        label: 'Documents',
        sub: 'What to write next, the upload, and what each change did',
        simpleSub: 'Add a document, and see what changed after the ones you added',
        icon: icon(FileText),
      },
    ],
  },
  {
    label: 'Measure',
    simpleLabel: 'Understand',
    tabs: [
      {
        id: 'adoption',
        label: 'Adoption',
        simpleLabel: 'Users',
        sub: 'Is it used, by whom, and do they come back',
        simpleSub: 'How many people ask, how often, and from which system',
        icon: icon(Users),
      },
      {
        id: 'quality',
        label: 'Quality',
        simpleLabel: 'Feedback',
        sub: 'Are answers rated helpful, and is that improving',
        simpleSub: 'Whether people mark the answers as helpful, and what they complain about',
        icon: icon(Star),
      },
      {
        id: 'tickets',
        label: 'Tickets',
        simpleLabel: 'Support tickets',
        sub: 'Where escalation happens, and whether the tickets arrive',
        simpleSub: 'How many people ended up asking a human, and whether the ticket arrived',
        icon: icon(Ticket),
      },
      {
        id: 'offtopic',
        label: 'Chit-chat',
        sub: 'Messages that were not questions about the systems',
        simpleSub: 'Greetings, personal questions, and everything out of scope',
        icon: icon(MessageCircle),
      },
    ],
  },
  {
    label: 'Operate',
    simpleLabel: 'Manage',
    tabs: [
      {
        // Stage timings and error strings, for whoever runs the service. The simple view
        // drops it and says the same thing in a sentence on the Overview.
        id: 'health',
        label: 'Health',
        sub: 'Latency per stage and failure rate — where the time goes',
        icon: icon(Activity),
        advanced: true,
      },
      {
        id: 'users',
        label: 'Accounts',
        sub: 'Who can sign in here, and which systems each of them may see',
        icon: icon(UserPlus),
        superadmin: true,
      },
      {
        // Superadmin-only for a stronger reason than Accounts: a key registered here lets an
        // application mint identities inside the assistant.
        id: 'integrations',
        label: 'Integrations',
        sub: 'Which applications may use the assistant, and the key each one signs with',
        icon: icon(Plug),
        superadmin: true,
      },
    ],
  },
];

export const ALL_TABS: Tab[] = NAV.flatMap((group) => group.tabs);

/** Whether this tab belongs in the rail at all, for this account and this view. */
export function visible(tab: Tab, { simple, superadmin }: { simple: boolean; superadmin: boolean }) {
  if (tab.superadmin && !superadmin) return false;
  if (tab.advanced && simple) return false;
  if (tab.simpleOnly && !simple) return false;
  return true;
}

/** Where the rail starts, and where a refused tab falls back to. */
export const defaultTab = (simple: boolean): string => (simple ? 'summary' : 'gaps');

export const labelOf = (tab: Tab, simple: boolean): string =>
  (simple && tab.simpleLabel) || tab.label;

/** The same, by id — for the Overview, which links to panels it names in prose and must
 *  call them what the rail calls them for this account. */
export const tabLabelOf = (id: string, simple: boolean): string => {
  const tab = ALL_TABS.find((t) => t.id === id);
  return tab ? labelOf(tab, simple) : id;
};

export const subOf = (tab: Tab, simple: boolean): string => (simple && tab.simpleSub) || tab.sub;
